/* oxlint-disable react/no-unstable-nested-components */

import { useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  columnFilteringFeature,
  createColumnHelper,
  createFilteredRowModel,
  createPaginatedRowModel,
  createSortedRowModel,
  filterFn_includesString,
  flexRender,
  globalFilteringFeature,
  rowPaginationFeature,
  rowSortingFeature,
  sortFn_alphanumeric,
  tableFeatures,
  useTable,
  type Column,
  type Row,
  type SortingState,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  ArrowUpDown,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Copy,
  Crosshair,
  Download,
  FileSpreadsheet,
  Search,
} from "lucide-react";

import { useChartTheme } from "@/components/charts/chart-theme";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { MapFeatureValue, RegionTimeSeries } from "@/features/explorer/api/contracts";
import { explorerQueryKeys, ANALYTICS_STALE_TIME_MS, ANALYTICS_GC_TIME_MS } from "@/features/explorer/api/query-keys";
import { getRegionTimeSeries } from "@/features/explorer/services/national-data-api";
import type { ExplorerSelection, MetricDefinition } from "@/features/explorer/types";
import { cn } from "@/lib/utils";

import { computeStatistics, formatMetricValue, formatZScore, formatZScoreInterpretation } from "../lib/map-utils";
import { copyJSONToClipboard, downloadCSV, downloadGeoJSON } from "../lib/export-engine";
import { Sparkline } from "./Sparkline";

export type TableRowData = {
  rank: number;
  geographyCode: string;
  name: string;
  value: number | null;
  formattedValue: string;
  zScore: string;
  vsMeanInterpretation: string;
  isSuppressed: boolean;
};

// Explicit TanStack Table v9 feature set for this view: global search,
// column sorting, and pagination (core row model is automatic in v9).
const tableViewFeatures = tableFeatures({
  columnFilteringFeature,
  globalFilteringFeature,
  rowSortingFeature,
  rowPaginationFeature,
  filteredRowModel: createFilteredRowModel(),
  sortedRowModel: createSortedRowModel(),
  paginatedRowModel: createPaginatedRowModel(),
  filterFns: { includesString: filterFn_includesString },
  sortFns: { alphanumeric: sortFn_alphanumeric },
});

type TableViewFeatures = typeof tableViewFeatures;

const columnHelper = createColumnHelper<TableViewFeatures, TableRowData>();

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 500] as const;

const PAGE_SIZE_ITEMS: Record<string, string> = Object.fromEntries(
  PAGE_SIZE_OPTIONS.map((size) => [String(size), String(size)]),
);

// Row virtualization only engages above this many visible rows. Small tables
// render the plain DOM (identical output, no measurement overhead); large
// page sizes (100/500) get a windowed tbody. Virtualization is renderer
// composition, never a Table feature: exports always read the full row model.
const VIRTUALIZE_ROW_THRESHOLD = 50;
const ROW_HEIGHT_ESTIMATE_PX = 64;

function DataRow({
  row,
  virtualIndex,
  measureRef,
}: {
  row: Row<TableViewFeatures, TableRowData>;
  virtualIndex?: number;
  measureRef?: (node: HTMLTableRowElement | null) => void;
}) {
  return (
    <tr data-index={virtualIndex} ref={measureRef} className='transition-colors hover:bg-muted/30'>
      {row.getAllCells().map((cell) => (
        <td key={cell.id} className={cellClassName(cell.column.id)}>
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </td>
      ))}
    </tr>
  );
}

type ExplorerTableViewProps = {
  metric: MetricDefinition;
  selection: ExplorerSelection;
  values: MapFeatureValue[];
  baseBoundaries: GeoJSON.FeatureCollection | null;
  isCompositeActive?: boolean;
  onSelectRegion?: (code: string, name: string) => void;
};

const cellClassName = (columnId: string) => {
  switch (columnId) {
    case "rank": {
      return "w-16 px-1.5 py-3 text-center";
    }
    case "trend": {
      return "w-24 px-1 py-3";
    }
    case "actions": {
      return "w-14 px-1 py-3";
    }
    default: {
      return "px-4 py-3";
    }
  }
};

function SortableHeader({ column, label }: { column: Column<TableViewFeatures, TableRowData, string>; label: string }) {
  const isSorted = column.getIsSorted();

  return (
    <Button
      variant='ghost'
      size='sm'
      onClick={() => {
        column.toggleSorting(isSorted === "asc");
      }}
      className='h-8 px-2 text-xs font-bold uppercase'>
      {label}
      {isSorted === "asc" ? (
        <ChevronUp className='ml-1 size-3.5' />
      ) : isSorted === "desc" ? (
        <ChevronDown className='ml-1 size-3.5' />
      ) : (
        <ArrowUpDown className='ml-1 size-3.5 opacity-50' />
      )}
    </Button>
  );
}

export function ExplorerTableView({
  metric,
  selection,
  values,
  baseBoundaries,
  isCompositeActive = false,
  onSelectRegion,
}: ExplorerTableViewProps) {
  "use no memo";

  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState<SortingState>([{ id: "value", desc: true }]);

  const activeUnit = isCompositeActive ? "score" : metric.unit;
  const activeMetricLabel = isCompositeActive ? "Custom Quality-of-Life Index" : metric.label;

  const uniqueValues = useMemo(() => {
    const valueMap = new Map<string, MapFeatureValue>();

    for (const value of values) {
      if (!valueMap.has(value.geographyCode)) {
        valueMap.set(value.geographyCode, value);
      }
    }

    return [...valueMap.values()];
  }, [values]);

  const validValues = useMemo(
    () =>
      uniqueValues
        .map((value) => value.value)
        .filter((value): value is number => value !== null && value !== undefined && !Number.isNaN(value)),
    [uniqueValues],
  );

  const stats = useMemo(() => computeStatistics(validValues), [validValues]);

  const { tokens } = useChartTheme();

  // One time-series query for every geography in the table (the API accepts an
  // arbitrary code list), so sorting, filtering, and pagination never churn on
  // per-row fetches. The payload is edge-cached and held for a month client-side.
  const allCodes = useMemo(() => uniqueValues.map((value) => value.geographyCode), [uniqueValues]);

  const { data: timeSeriesData, isPending: isTimeSeriesPending } = useQuery<RegionTimeSeries>({
    queryKey: explorerQueryKeys.regionTimeSeries({
      metricId: selection.metricId,
      geographyLevel: selection.geographyLevel,
      statisticId: selection.statisticId,
      geographyCodes: allCodes,
    }),
    queryFn: () =>
      getRegionTimeSeries({
        metricId: selection.metricId,
        geographyLevel: selection.geographyLevel,
        statisticId: selection.statisticId,
        geographyCodes: allCodes,
      }),
    enabled: allCodes.length > 0,
    staleTime: ANALYTICS_STALE_TIME_MS,
    gcTime: ANALYTICS_GC_TIME_MS,
    refetchOnWindowFocus: false,
  });

  const seriesByCode = useMemo(() => {
    const map = new Map<string, number[]>();
    for (const series of timeSeriesData?.series ?? []) {
      map.set(
        series.geographyCode,
        series.points.flatMap((point) => (point.value !== null && Number.isFinite(point.value) ? [point.value] : [])),
      );
    }
    return map;
  }, [timeSeriesData]);

  const codeToName = useMemo(() => {
    const nameMap = new Map<string, string>();

    if (!baseBoundaries) {
      return nameMap;
    }

    for (const feature of baseBoundaries.features) {
      const properties = feature.properties;

      if (!properties) {
        continue;
      }

      const code: unknown = properties.geographyCode;
      const name: unknown = properties.name;

      if (typeof code === "string" && typeof name === "string") {
        nameMap.set(code, name);
      }
    }

    return nameMap;
  }, [baseBoundaries]);

  const tableData = useMemo<TableRowData[]>(() => {
    const sortedValues = uniqueValues.toSorted((a, b) => (b.value ?? -Infinity) - (a.value ?? -Infinity));

    return sortedValues.map((item, index) => {
      const value = item.value;

      return {
        rank: index + 1,
        geographyCode: item.geographyCode,
        name: codeToName.get(item.geographyCode) ?? item.geographyCode,
        value,
        formattedValue: formatMetricValue(value, activeUnit),
        zScore: stats && value !== null ? formatZScore(value, stats.mean, stats.stdDev) : "N/A",
        vsMeanInterpretation:
          stats && value !== null ? formatZScoreInterpretation(value, stats.mean, stats.stdDev, activeUnit) : "",
        isSuppressed: item.isSuppressed || value === null,
      };
    });
  }, [activeUnit, codeToName, stats, uniqueValues]);

  const columns = useMemo(
    () =>
      columnHelper.columns([
        columnHelper.accessor("rank", {
          header: ({ column }) => (
            <Button
              variant='ghost'
              size='sm'
              onClick={() => {
                column.toggleSorting(column.getIsSorted() === "asc");
              }}
              className='h-8 px-2 text-xs font-bold uppercase'>
              # Rank
              {column.getIsSorted() === "asc" ? (
                <ChevronUp className='ml-1 size-3.5' />
              ) : column.getIsSorted() === "desc" ? (
                <ChevronDown className='ml-1 size-3.5' />
              ) : (
                <ArrowUpDown className='ml-1 size-3.5 opacity-50' />
              )}
            </Button>
          ),
          cell: (info) => <span className='font-mono font-bold text-muted-foreground'>{info.getValue()}</span>,
        }),
        columnHelper.accessor("name", {
          header: ({ column }) => <SortableHeader column={column} label='Area / District' />,
          cell: (info) => {
            const row = info.row.original;

            return (
              <div className='flex flex-col'>
                <span className='font-bold text-foreground'>{row.name}</span>
                <span className='font-mono text-xs text-muted-foreground'>{row.geographyCode}</span>
              </div>
            );
          },
        }),
        columnHelper.accessor("value", {
          header: ({ column }) => (
            <Button
              variant='ghost'
              size='sm'
              onClick={() => {
                column.toggleSorting(column.getIsSorted() === "asc");
              }}
              className='h-8 px-2 text-xs font-bold uppercase'>
              {activeMetricLabel}
              {column.getIsSorted() === "asc" ? (
                <ChevronUp className='ml-1 size-3.5' />
              ) : column.getIsSorted() === "desc" ? (
                <ChevronDown className='ml-1 size-3.5' />
              ) : (
                <ArrowUpDown className='ml-1 size-3.5 opacity-50' />
              )}
            </Button>
          ),
          cell: (info) => {
            const row = info.row.original;

            if (row.isSuppressed) {
              return <span className='text-xs italic text-muted-foreground'>Suppressed</span>;
            }

            return (
              <div className='flex flex-col'>
                <span className='font-mono text-base font-extrabold text-primary'>{row.formattedValue}</span>

                {row.zScore !== "N/A" && <span className='font-mono text-xs text-muted-foreground'>{row.zScore}</span>}
              </div>
            );
          },
        }),
        columnHelper.display({
          id: "trend",
          header: "Trend",
          enableSorting: false,
          enableGlobalFilter: false,
          cell: (info) => {
            const trendValues = seriesByCode.get(info.row.original.geographyCode) ?? [];

            if (trendValues.length < 2) {
              return (
                <div className='h-6 w-20'>
                  {isTimeSeriesPending ? (
                    <div className='size-full animate-pulse rounded bg-muted/40' aria-hidden='true' />
                  ) : (
                    <span className='text-xs text-muted-foreground'>—</span>
                  )}
                </div>
              );
            }

            const up = (trendValues.at(-1) ?? trendValues[0]) >= trendValues[0];
            return (
              <div className='flex h-6 w-20 items-center'>
                <Sparkline
                  points={trendValues}
                  width={68}
                  height={24}
                  color={up ? tokens.success : tokens.destructive}
                />
              </div>
            );
          },
        }),
        columnHelper.accessor("vsMeanInterpretation", {
          header: ({ column }) => <SortableHeader column={column} label='vs UK Mean' />,
          sortFn: (rowA, rowB) => {
            const valueA = rowA.original.value;
            const valueB = rowB.original.value;

            if (valueA === null || valueA === undefined) {
              return 1;
            }
            if (valueB === null || valueB === undefined) {
              return -1;
            }
            return valueA - valueB;
          },
          cell: (info) => {
            const row = info.row.original;

            if (row.isSuppressed || !stats || row.value === null) {
              return null;
            }

            const isAboveMean = row.value >= stats.mean;

            return (
              <Badge
                variant='outline'
                className={cn(
                  "rounded-xs px-2 py-0.5 font-mono text-xs font-semibold",
                  isAboveMean
                    ? "border-success/40 bg-success/10 text-success"
                    : "border-destructive/40 bg-destructive/10 text-destructive",
                )}>
                {row.vsMeanInterpretation}
              </Badge>
            );
          },
        }),
        columnHelper.display({
          id: "actions",
          header: "",
          enableSorting: false,
          enableGlobalFilter: false,
          cell: (info) => {
            const row = info.row.original;

            if (!onSelectRegion) {
              return null;
            }

            return (
              <Button
                variant='ghost'
                size='icon'
                onClick={() => {
                  onSelectRegion(row.geographyCode, row.name);
                }}
                title={`Compare ${row.name} on the map`}
                aria-label={`Compare ${row.name} on the map`}
                className='size-8 cursor-pointer text-primary hover:bg-primary/10'>
                <Crosshair className='size-4' />
              </Button>
            );
          },
        }),
      ]),
    [activeMetricLabel, onSelectRegion, stats, seriesByCode, isTimeSeriesPending, tokens],
  );

  // TanStack Table is currently treated as React Compiler-incompatible.
  // oxlint-disable-next-line react-hooks-js/incompatible-library
  const table = useTable({
    features: tableViewFeatures,
    data: tableData,
    columns,
    state: {
      sorting,
      globalFilter,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 25,
      },
    },
  });

  const handleExportCSV = () => {
    const filteredRows = table.getFilteredRowModel().rows.map((row) => row.original);

    const headers = [
      "Rank",
      "Geography Code",
      "Name",
      activeMetricLabel,
      "Unit",
      "Z-Score",
      "vs UK Mean Interpretation",
    ];

    const csvRows = filteredRows.map((row) => [
      row.rank,
      row.geographyCode,
      row.name,
      row.value,
      activeUnit,
      row.zScore,
      row.vsMeanInterpretation,
    ]);

    const filename = `metricmap_${selection.geographyLevel}_${
      isCompositeActive ? "composite_index" : selection.metricId
    }_2024.csv`;

    downloadCSV(filename, headers, csvRows);
  };

  const handleExportGeoJSON = () => {
    if (!baseBoundaries) {
      return;
    }

    const valueMap = new Map(uniqueValues.map((value) => [value.geographyCode, value.value]));

    const mergedGeoJSON: GeoJSON.FeatureCollection = {
      ...baseBoundaries,
      features: baseBoundaries.features.map((feature) => {
        const properties = (feature.properties ?? {}) as Record<string, unknown>;

        const geographyCode =
          (typeof properties.geographyCode === "string" ? properties.geographyCode : undefined) ??
          (typeof properties.code === "string" ? properties.code : undefined) ??
          (typeof properties.gssCode === "string" ? properties.gssCode : undefined) ??
          "";

        return {
          ...feature,
          properties: {
            ...properties,
            geographyCode,
            metricId: selection.metricId,
            metricLabel: activeMetricLabel,
            value: valueMap.get(geographyCode) ?? null,
            unit: activeUnit,
          },
        };
      }),
    };

    const filename = `metricmap_${selection.geographyLevel}_boundaries.geojson`;

    downloadGeoJSON(filename, mergedGeoJSON);
  };

  const handleCopyJSON = async () => {
    const filteredRows = table.getFilteredRowModel().rows.map((row) => row.original);

    await copyJSONToClipboard(filteredRows);
  };

  const visibleRows = table.getRowModel().rows;
  const filteredRows = table.getFilteredRowModel().rows;

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const shouldVirtualize = visibleRows.length > VIRTUALIZE_ROW_THRESHOLD;
  // TanStack Virtual is currently treated as React Compiler-incompatible,
  // same as TanStack Table above: prototype APIs that cannot memoize safely.
  // oxlint-disable-next-line react-hooks-js/incompatible-library
  const rowVirtualizer = useVirtualizer<HTMLDivElement, HTMLTableRowElement>({
    count: visibleRows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT_ESTIMATE_PX,
    overscan: 10,
    getItemKey: (index) => visibleRows.at(index)?.id ?? index,
  });
  const virtualItems = rowVirtualizer.getVirtualItems();
  const firstVirtualItem = virtualItems.at(0);
  const lastVirtualItem = virtualItems.at(-1);
  const virtualTopPad = firstVirtualItem ? firstVirtualItem.start : 0;
  const virtualBottomPad = lastVirtualItem ? rowVirtualizer.getTotalSize() - lastVirtualItem.end : 0;

  return (
    <div className='w-full space-y-4 font-sans'>
      <div className='flex flex-col items-stretch justify-between gap-3 rounded-lg border border-border bg-card p-4 shadow-sm sm:flex-row sm:items-center'>
        <div className='relative flex-1'>
          <Search className='absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground' />

          <input
            type='text'
            value={globalFilter}
            onChange={(event) => {
              setGlobalFilter(event.target.value);
            }}
            placeholder='Search council by name or code...'
            className='h-10 w-full rounded-md border border-border bg-muted/40 pl-9 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none'
          />
        </div>

        <div className='hidden flex-wrap items-center gap-2 sm:flex'>
          <Button
            variant='outline'
            size='sm'
            onClick={handleExportCSV}
            className='h-10 cursor-pointer gap-1.5 px-3 text-xs font-semibold'>
            <FileSpreadsheet className='size-4 text-success' />
            CSV Export
          </Button>

          <Button
            variant='outline'
            size='sm'
            onClick={handleExportGeoJSON}
            disabled={!baseBoundaries}
            className='h-10 cursor-pointer gap-1.5 px-3 text-xs font-semibold'>
            <Download className='size-4 text-primary' />
            GeoJSON
          </Button>

          <Button
            variant='outline'
            size='sm'
            onClick={() => {
              void handleCopyJSON();
            }}
            className='h-10 cursor-pointer gap-1.5 px-3 text-xs font-semibold'>
            <Copy className='size-4 text-muted-foreground' />
            Copy JSON
          </Button>
        </div>
      </div>

      <div className='overflow-hidden rounded-lg border border-border bg-card shadow-sm'>
        <div
          ref={scrollRef}
          data-virtualized={shouldVirtualize}
          className={shouldVirtualize ? "max-h-[70vh] overflow-auto" : "overflow-x-auto"}>
          <table className='w-full border-collapse text-left text-sm'>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className='border-b border-border bg-muted/40 text-muted-foreground'>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className={cn(
                        `${cellClassName(header.column.id)} text-xs font-bold uppercase`,
                        shouldVirtualize && "sticky top-0 z-10 bg-muted",
                      )}>
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>

            <tbody className='divide-y divide-border'>
              {visibleRows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className='px-4 py-8 text-center text-sm text-muted-foreground'>
                    No council matching &quot;{globalFilter}&quot; found.
                  </td>
                </tr>
              ) : shouldVirtualize ? (
                <>
                  {virtualTopPad > 0 && (
                    <tr data-spacer='top' aria-hidden='true'>
                      <td
                        colSpan={columns.length}
                        aria-hidden='true'
                        style={{ height: virtualTopPad, padding: 0, border: 0 }}
                      />
                    </tr>
                  )}
                  {virtualItems.map((virtualItem) => {
                    const row = visibleRows[virtualItem.index];
                    return (
                      <DataRow
                        key={row.id}
                        row={row}
                        virtualIndex={virtualItem.index}
                        measureRef={rowVirtualizer.measureElement}
                      />
                    );
                  })}
                  {virtualBottomPad > 0 && (
                    <tr data-spacer='bottom' aria-hidden='true'>
                      <td
                        colSpan={columns.length}
                        aria-hidden='true'
                        style={{ height: virtualBottomPad, padding: 0, border: 0 }}
                      />
                    </tr>
                  )}
                </>
              ) : (
                visibleRows.map((row) => <DataRow key={row.id} row={row} />)
              )}
            </tbody>
          </table>
        </div>

        <div className='flex flex-col items-center justify-between gap-3 border-t border-border bg-muted/20 px-4 py-3 text-xs text-muted-foreground sm:flex-row'>
          <div>
            Showing <strong className='text-foreground'>{visibleRows.length}</strong> of{" "}
            <strong className='text-foreground'>{filteredRows.length}</strong> councils
          </div>

          <div className='flex items-center gap-4'>
            <div className='flex items-center gap-2'>
              <span id='table-page-size-label'>Show</span>

              <Select
                value={String(table.state.pagination.pageSize)}
                onValueChange={(value) => {
                  table.setPageSize(Number(value));
                }}
                items={PAGE_SIZE_ITEMS}>
                <SelectTrigger aria-labelledby='table-page-size-label' className='h-8 w-20 px-2 text-xs'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <SelectItem key={size} value={String(size)}>
                        {size}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            <div className='flex items-center gap-1'>
              <Button
                variant='outline'
                size='icon'
                onClick={() => {
                  table.previousPage();
                }}
                disabled={!table.getCanPreviousPage()}
                className='size-8 cursor-pointer'>
                <ChevronLeft className='size-4' />
                <span className='sr-only'>Previous Page</span>
              </Button>

              <span className='px-2 font-mono font-bold text-foreground'>
                Page {table.state.pagination.pageIndex + 1} of {table.getPageCount() || 1}
              </span>

              <Button
                variant='outline'
                size='icon'
                onClick={() => {
                  table.nextPage();
                }}
                disabled={!table.getCanNextPage()}
                className='size-8 cursor-pointer'>
                <ChevronRight className='size-4' />
                <span className='sr-only'>Next Page</span>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
